'use client';

import { useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { RoomInfoPanel } from '@/components/public/room-info-panel';
import { ImageLightbox } from '@/components/public/image-lightbox';
import type { LightboxImage } from '@/components/public/image-lightbox';
import type { Room } from '@/types/database';

export default function CountDanielsChamberContent({ room }: { room: Room }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const t = useTranslations('roomContent.count-daniels-chamber');

  const GALLERY_IMAGES: LightboxImage[] = [
    { src: '/design-images/rooms/count-daniels-chamber/detail.jpg', alt: t('alt.detail'), width: 1920, height: 1343 },
    { src: '/design-images/rooms/count-daniels-chamber/gallery-1.jpg', alt: t('alt.gallery1'), width: 1920, height: 2699 },
    { src: '/design-images/rooms/count-daniels-chamber/gallery-2.jpg', alt: t('alt.gallery2'), width: 1920, height: 1281 },
    { src: '/design-images/rooms/count-daniels-chamber/gallery-5.jpg', alt: t('alt.gallery5'), width: 1920, height: 1283 },
  ];

  return (
    <div id="room-content" className="w-full flex flex-col">
      {/* White spacer between hero and first section */}
      <div className="bg-white h-2 w-full" />

      {/* Section: Room Benefits */}
      <section className="bg-white w-full">
        <div className="max-w-[1500px] mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="w-full flex items-center py-6 px-6 md:px-8 lg:px-12">
              <div className="text-black w-full">
                <div className="tiptap-content text-base md:text-lg leading-relaxed">
                    <h1 className="font-heading font-bold mb-4 text-4xl md:text-5xl"><strong>{t('benefits.titleLine1')}</strong><br /><strong>{t('benefits.titleLine2')}</strong></h1>
                    <p className="mb-4">&nbsp;</p>
                    <p className="mb-4">{t('benefits.p1')}</p>
                </div>
                <RoomInfoPanel room={room} />
              </div>
            </div>
            <div
              className="w-full h-full min-h-[400px] md:min-h-[500px] relative cursor-pointer"
              onClick={() => setLightboxIndex(0)}
            >
              <Image
                src="/design-images/rooms/count-daniels-chamber/detail.jpg"
                alt={t('alt.detail')}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
          </div>
        </div>
      </section>
      <div className="bg-white h-2 w-full" />

      {/* Section: Side-by-side */}
      <section className="bg-white w-full">
        <div className="max-w-[1500px] mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div
              className="w-full h-full min-h-[400px] md:min-h-[500px] relative cursor-pointer"
              onClick={() => setLightboxIndex(1)}
            >
              <Image
                src="/design-images/rooms/count-daniels-chamber/gallery-1.jpg"
                alt={t('alt.gallery1')}
                fill
                className="object-cover"
                style={{objectPosition: '50% 15%'}}
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
            <div className="w-full flex items-center py-8 md:py-12 px-6 md:px-8 lg:px-12">
              <div className="text-black w-full">
                <div className="tiptap-content text-base md:text-lg leading-relaxed">
                    <h1 className="font-heading font-bold mb-6 text-4xl md:text-5xl">{t('story.title')}</h1>
                    <div className="w-16 h-1 bg-[#801515] mb-8" />
                    <p className="mb-6 text-lg md:text-xl leading-relaxed font-light italic text-gray-700">{t('story.p1')}</p>
                    <p className="mb-6 text-base md:text-lg leading-relaxed text-gray-700">{t('story.p2')}</p>
                    <p className="text-base md:text-lg leading-relaxed text-gray-700">{t('story.p3')}</p>
                </div>
              </div>
            </div>
        </div>
        </div>
      </section>
      <div className="bg-white h-2 w-full" />

      {/* Single image */}
      <div className="mt-2 cursor-pointer" onClick={() => setLightboxIndex(2)}>
        <Image
          src="/design-images/rooms/count-daniels-chamber/gallery-2.jpg"
          alt={t('alt.gallery2')}
          width={1920}
          height={1281}
          className="w-full h-auto"
          sizes="100vw"
        />
      </div>

      <section className="w-full mt-2 cursor-pointer" onClick={() => setLightboxIndex(3)}>
        <Image
          src="/design-images/rooms/count-daniels-chamber/gallery-5.jpg"
          alt={t('alt.gallery5')}
          width={1920}
          height={1283}
          className="w-full h-auto"
          sizes="100vw"
        />
      </section>

      {/* Fullscreen gallery lightbox */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={GALLERY_IMAGES}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
